import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import VimeoPlayer from 'react-player/vimeo';
import { useTranslation } from '@klv/hooks/useTranslation';
import { debounce } from 'lodash-es';

import { useModal } from '@klv/context/modal';
import { EnhancedCard } from '@klv/molecules/enhanced-card';
import Modal from '@klv/organisms/modal';

import { generateClassName, hash } from '@klv/shared/utils/functions';
import { Wrapper } from '@klv/utils/wrapper';
import * as styles from './styles/enhancedCarousel.module.scss';
import type { IEnhancedCarousel,  ISetUpCarouselIntersectionObserverProps } from './types';
import { getBackgroundType } from '@klv/utils/wrapper/functions';
import CarouselButton from '@klv/molecules/carousel-button';
import { useWindowDimensions } from '@klv/hooks/useWindowDimensions';
import type { TColorVariant } from '@klv/shared/styles/types';

const getCarouselTrackPadding = (carouselTrackRef: React.RefObject<HTMLDivElement | null>) => {
  if (
    window.innerWidth >= 2015
    && carouselTrackRef.current
    && carouselTrackRef.current.childElementCount > 3
  ) {
    const visibleChildren = Array.from(carouselTrackRef.current.children).filter((child) => (child as HTMLElement).offsetParent !== null);

    if (visibleChildren.length > 3) {
      return Math.floor(parseFloat(window.getComputedStyle(carouselTrackRef.current).getPropertyValue('padding-left').replace('px', '')));
    }
  }

  return 0;
};

const setUpObserver = ({
  carouselTrackPadding,
  carouselTrackRef,
  intersectionHandler,
  observerRef,
}: ISetUpCarouselIntersectionObserverProps) => {
  if (observerRef.current) {
    observerRef.current.disconnect();
  }

  const observer = new IntersectionObserver(intersectionHandler, {
    root: carouselTrackRef.current,
    rootMargin: `0px -${carouselTrackPadding}px`,
    threshold: 1,
  });

  if (carouselTrackRef.current) {
    Array.from(carouselTrackRef.current.children).forEach((child) => {
      observer.observe(child);
    });
  }

  observerRef.current = observer;
};

export const EnhancedCarousel = ({
  className = '',
  customPadding,
  backgroundColor,
  desktopCarouselBehavior,
  cardHeaderStyle,
  cardSize = 's',
  cardStyle,
  ctaStyle,
  fadeIn,
  headline,
  cards,
  mobileCarouselBehavior,
  stackAcross,
  ...props
}: IEnhancedCarousel) => {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();
  const backgroundType: TColorVariant = getBackgroundType(backgroundColor);
  const carouselId = `${hash(JSON.stringify({ backgroundColor,  stackAcross, headline, cardSize, cardStyle, ctaStyle }))}`;
  const carouselElId = `carousel-${carouselId}`;
  const carouselTrackRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const [enableBackArrow, setEnableBackArrow] = useState(true);
  const modalStore = useModal();
  const { modalState } = modalStore;
  const canShowModal = (modalState.visibleModalId === carouselId);
  const [currentModalVideoUrl, setCurrentModalVideoUrl] = useState('');
  const [currentModalTitle, setCurrentModalTitle] = useState('');
  
  const onOpenVideo = useCallback((url: string, title: string) => {
    setCurrentModalVideoUrl(url);
    setCurrentModalTitle(title);
    modalStore.open(carouselId);
  }, [setCurrentModalVideoUrl, setCurrentModalTitle, modalStore, carouselId]);


  const [isMobile, setIsMobile] = useState(width < 768);
  const [enableForwardArrow, setEnableForwardArrow] = useState(cards && cards?.length > 2 ? true : false);
  const [carouselBehavior, setCarouselBehavior] = useState( width < 768 ? mobileCarouselBehavior : desktopCarouselBehavior);

  const getTrueFirstChild = useCallback(() => {
    const firstChild = carouselTrackRef?.current?.firstChild as HTMLElement;
    const secondChild = firstChild?.nextElementSibling as HTMLElement;

    if (!firstChild) return null;

    const firstElementStyle = window.getComputedStyle(firstChild);
    return firstElementStyle.display === 'none' ? secondChild : firstChild;
  }, [carouselTrackRef]);

  const scroll = useCallback(
    (distance: number) => {
      if (carouselTrackRef?.current) {
        carouselTrackRef.current.scrollBy({ left: distance, behavior: 'smooth' });
      }
    }, [carouselTrackRef]);

  // scroll back one card 
  const back = useCallback(() => {
    const firstChild = getTrueFirstChild();
    if (carouselTrackRef?.current && firstChild) {
      scroll(0 - (firstChild).offsetWidth);
    }
  }, [getTrueFirstChild, scroll]);

  // scroll forward one card
  const forward = useCallback(() => {
    const firstChild = getTrueFirstChild();
    if (carouselTrackRef?.current && firstChild) {
      scroll((firstChild).offsetWidth);
    }
  }, [getTrueFirstChild, scroll]);

  // Determine the card in view and update state of arrows
  useEffect(() => {
    const handleIntersection: IntersectionObserverCallback = (entries, observer) => {
      entries.forEach((entry) => {
        if (observer.root !== null) {
          const firstChild = getTrueFirstChild();
          if (entry.target === firstChild) {
            setEnableBackArrow(entry.intersectionRatio < 1);
          }
          if
          (entry.target === observer.root.lastElementChild) {
            setEnableForwardArrow(entry.intersectionRatio < 1);
          }
        }
      });
    };

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    setUpObserver({
      carouselTrackPadding: getCarouselTrackPadding(carouselTrackRef),
      carouselTrackRef,
      intersectionHandler: handleIntersection,
      observerRef,
    });

    const handleResize = debounce(() => {
      setUpObserver({
        carouselTrackPadding: getCarouselTrackPadding(carouselTrackRef),
        carouselTrackRef,
        intersectionHandler: handleIntersection,
        observerRef,
      });
    }, 500, { trailing: true });

    window.addEventListener('resize', handleResize);

    return () => {
      observerRef?.current?.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Update carousel behavior based on screen width
  useEffect(() => {
    setCarouselBehavior(width < 768 ? mobileCarouselBehavior : desktopCarouselBehavior);
    setIsMobile(width < 768);
  }, [desktopCarouselBehavior, mobileCarouselBehavior, width]);

  return (
    <Wrapper contained={false} customPadding={customPadding} fadeIn={fadeIn ? .25 : 0} backgroundColor={backgroundColor}>
      <section
        aria-roledescription="carousel"
        aria-label={headline || 'carousel'}
        className={generateClassName([className, styles.enhancedCarousel])}
        {...props}
        >
        {headline && (
          <div className={generateClassName(['grid'])}>
            <h2 className={generateClassName(['h2', styles.headline])}>{headline}</h2>
          </div>
        )}

        {/* Scroll Carousel */}
        {/* Card size determined by cardSize prop */}
        {carouselBehavior === 'scroll' && (
          <>
            <div
              className={generateClassName([styles.carousel])}
              aria-live="polite"
              id={carouselElId}
            >
              <div className={styles.carouselTrack} ref={carouselTrackRef}>
                {cards  && ( cards.map((card, index) => {
                  const getCardSize = `cardSize${String(cardSize === 'multisize'  ? card.multiSizeCard : cardSize).toUpperCase()}` as keyof typeof styles;
                  return (
                    <div 
                      className={generateClassName([
                        (cardSize ? styles[getCardSize] : ''),
                        cardSize === 'multisize' && card && card.multiSizeCard === 'm' && styles.multiSizeM,
                      ])
                      }
                      key={`${cardSize}-${index}`}
                    >
                    <EnhancedCard
                      {...card}
                      backgroundColor={backgroundColor}
                      ctaStyle={ctaStyle}
                      cardHeaderStyle={cardHeaderStyle}
                      cardStyle={cardStyle}
                      onOpenVideo={onOpenVideo}
                      isMobile={isMobile}
                    />
                  </div>
                  );
                }))}
              </div>
            </div>
            {/* Arrow buttons */}
            <Wrapper className={styles.buttonWrapper} contained={false} inlinePadding={true} customPadding={customPadding}>
              <div 
                className={generateClassName([styles.carouselButtonContainer])}
                style={{
                  '--disabled-color-arrow': backgroundType === 'light' ? 'var(--color-charcoal)' : 'var(--color-white)',
                  '--disabled-color-background': backgroundType === 'light' ? 'rgba(44, 45, 46, 0.10)' : 'rgba(255, 255, 255, 0.10)',
                } as React.CSSProperties}
              >
                <CarouselButton
                  className={generateClassName([styles.styledCarouselButton, !enableBackArrow ? styles.disabledCarouselButton : ''])}
                  colorVariant={backgroundType === 'light' ? 'dark' : 'light'}
                  direction='left'
                  onClick={back}
                />
                <CarouselButton
                  className={generateClassName([styles.styledCarouselButton, !enableForwardArrow ? styles.disabledCarouselButton : ''])}
                  colorVariant={backgroundType === 'light' ? 'dark' : 'light'}
                  direction='right'
                  onClick={forward}
                />
              </div>
            </Wrapper>
          </>
        )}
        {/* Stack crousel */}
        {/* Card size determined by stackAcross prop */}
        {carouselBehavior === 'stack' && stackAcross && (
          <div 
            className={generateClassName(['grid', styles.stackedGrid, Number(stackAcross) === 3 ? styles.threeAcross : '', Number(stackAcross) === 4 ? styles.fourAcross : ''])}
            style={{
              '--ec-set-grid-column': `repeat(${(stackAcross)}, 2fr)`,
            } as React.CSSProperties}
          >
            {cards && cards.length > 0 && ( cards.map((card, index) => {
              return (
                <div 
                  className={generateClassName([styles.carouselItem])}
                  key={`${stackAcross}-${index}`}
                >
                  <EnhancedCard
                    {...card}
                    backgroundColor={backgroundColor}
                    ctaStyle={ctaStyle}
                    cardHeaderStyle={cardHeaderStyle}
                    cardStyle={cardStyle}
                    onOpenVideo={onOpenVideo}
                  />
              </div>
              );
            }))}
          </div>
        )}
          
        {/* Modal for video player */}
        <Modal
          contentPadding="30"
          close={modalStore.close}
          show={canShowModal}
          size="constrained"
          backgroundColor={backgroundType}
          isMediaModal
          title={currentModalTitle}
        >
          <VimeoPlayer
            className={generateClassName([styles.styledVimeoPlayer, 'react-player'])}
            config={{ playerOptions:{ cc: t('langCodes_recaptchaLanguage') === 'en' ? 'false' : 'true' } } }  
            url={currentModalVideoUrl}
            controls
            playing
            loop={false}
            width="100%"
            height="auto"
          />
        </Modal>

      </section>
    </Wrapper>
  );
};
